import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { useUserStore } from "../../store/user.store";
import { useCartStore } from "../../store/cart.store";
import { UserRole } from "../../types/index"; 
import { DynamicCart } from "../cart/DynamicCart"; 
import { DynamicLocation } from "../location/DynamicLocation"; 
import { LocationModal } from "../location/LocationModal";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { toast } from "react-hot-toast";
import { Menu, X, User, LogOut, ChevronDown, LayoutDashboard, Truck, ChefHat } from "lucide-react";

export const Navbar: React.FC = () => {
  const { user, signOut: logout } = useAuth();
  const { userProfile, setUserProfile } = useUserStore();
  const { items } = useCartStore();
  const navigate = useNavigate();
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  
  const currentRole = userProfile?.currentRole || "customer";
  const showRoleSwitcher = userProfile?.roles && userProfile.roles.length > 1;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ✅ PERSIST ROLE TO DB & ROUTE CORRECTLY
  const handleSwitchRole = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRole = e.target.value as UserRole;
    if (!user?.uid) return;

    try {
      if (setUserProfile && userProfile) {
        setUserProfile({ ...userProfile, currentRole: newRole });
      }
      await updateDoc(doc(db, "users", user.uid), { currentRole: newRole });

      const dashboardPaths: Record<UserRole, string> = {
        customer: "/",
        chef: "/chef/dashboard",
        admin: "/admin/dashboard",
        deliveryAgent: "/deliveryagent/dashboard",
        cashier: "/cashier/dashboard",
        kitchenOwner: "/kitchen/dashboard",
      };
      
      const targetPath = dashboardPaths[newRole] || "/";
      navigate(targetPath);
      setIsMobileMenuOpen(false);
      toast.success(`Switched to ${formatRole(newRole)}`);
    } catch (error) {
      console.error("Role switch failed", error);
      toast.error("Failed to switch role");
    }
  };

  const handleLogout = async () => {
    if (window.confirm("Logout?")) {
      await logout();
      navigate("/login");
      setIsMobileMenuOpen(false);
    }
  };

  const formatRole = (role: string) => role.charAt(0).toUpperCase() + role.slice(1).replace(/([A-Z])/g, ' $1').trim();

  const getDashboardPath = (role: string) => {
    if (role === "deliveryAgent") return "/deliveryagent/dashboard";
    if (role === "kitchenOwner") return "/kitchen/dashboard";
    return `/${role}/dashboard`;
  };


  return (
    <>
      <LocationModal />
      
      {/* SOLID FIXED NAVBAR */}
      <nav className="fixed top-0 left-0 right-0 w-full bg-white border-b border-gray-200 z-50 h-16 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-full flex items-center justify-between gap-2">
          
          {/* LEFT: Logo & Location */}
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-6 min-w-0 flex-1">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img src="/ruchira-logo.png" alt="Ruchira" className="h-8 w-8 rounded-full object-cover" />
              <span className="font-black text-xl text-orange-600 tracking-tighter hidden md:block">RUCHIRA</span>
            </Link>
            <div className="min-w-0 ml-0.5 sm:ml-2">
               <DynamicLocation />
            </div>
          </div>

          {/* RIGHT: Actions */}
          <div className="flex items-center gap-2 sm:gap-3 md:gap-6 shrink-0">
            
            {showRoleSwitcher && (
              <div className="hidden md:flex items-center bg-orange-50 rounded-lg px-2 py-1 border border-orange-100">
                <span className="text-[10px] font-bold text-orange-400 uppercase mr-2">Role:</span>
                <select 
                  value={currentRole}
                  onChange={handleSwitchRole}
                  className="bg-transparent text-sm font-bold text-orange-800 outline-none cursor-pointer uppercase"
                >
                  {userProfile?.roles.map(r => <option key={r} value={r}>{formatRole(r)}</option>)}
                </select>
              </div>
            )}

            <DynamicCart />

            {/* Desktop Profile */}
            <div className="hidden md:block relative" ref={profileRef}>
              {user ? (
                <>
                  <button onClick={() => setIsProfileOpen(!isProfileOpen)} className="flex items-center gap-2 hover:bg-gray-50 p-1.5 rounded-full transition-colors border border-transparent hover:border-gray-200">
                    <img src={`https://ui-avatars.com/api/?name=${userProfile?.name || 'User'}&background=random`} className="h-8 w-8 rounded-full" alt="" />
                    <ChevronDown size={16} className="text-gray-400" />
                  </button>

                  {isProfileOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl py-2 border border-gray-100 animate-in fade-in slide-in-from-top-2">
                      <div className="px-4 py-2 border-b border-gray-100 mb-1">
                        <p className="font-bold text-gray-800 truncate">{userProfile?.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                      <Link to="/profile" className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                        <User size={16} /> Profile
                      </Link>
                      {currentRole !== 'customer' && (
                        <Link to={getDashboardPath(currentRole)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <LayoutDashboard size={16} /> Dashboard
                        </Link>
                      )}
                      <button onClick={handleLogout} className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                        <LogOut size={16} /> Logout
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex gap-2">
                  <Link to="/login" className="min-h-[44px] inline-flex items-center px-4 py-2 text-sm font-bold text-gray-600 hover:text-orange-600">Login</Link>
                  <Link to="/signup" className="min-h-[44px] inline-flex items-center px-4 py-2 text-sm font-bold bg-orange-500 text-white rounded-lg hover:bg-orange-600 shadow-md">Sign Up</Link>
                </div>
              )}
            </div>

            {/* Mobile Toggle */}
            <button
              type="button"
              className="md:hidden min-h-[44px] min-w-[44px] p-2 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg"
              aria-label="Open menu"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* SPACER FOR FIXED HEADER */}
      <div className="h-16"></div>

      {/* MOBILE DRAWER */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex justify-end md:hidden">
          <div className="w-[85%] max-w-sm bg-white h-full p-5 shadow-2xl animate-in slide-in-from-right duration-200 flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <span className="font-black text-xl text-orange-600">MENU</span>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 bg-gray-100 rounded-full"><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-6">
              {user ? (
                <>
                  <div className="bg-orange-50 p-4 rounded-xl border border-orange-100 flex items-center gap-3">
                    <img src={`https://ui-avatars.com/api/?name=${userProfile?.name}&background=random`} className="h-12 w-12 rounded-full" alt=""/>
                    <div className="overflow-hidden">
                      <p className="font-bold text-gray-900 truncate">{userProfile?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                  </div>

                  {showRoleSwitcher && (
                    <div>
                      <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">Switch Profile</label>
                      <div className="relative">
                        <select 
                          value={currentRole}
                          onChange={handleSwitchRole}
                          className="w-full appearance-none bg-white border-2 border-gray-200 text-gray-800 font-bold py-3 px-4 rounded-xl outline-none focus:border-orange-500"
                        >
                          {userProfile?.roles.map(r => <option key={r} value={r}>{formatRole(r)}</option>)}
                        </select>
                        <ChevronDown className="absolute right-4 top-3.5 text-gray-400 pointer-events-none" size={16} />
                      </div>
                    </div>
                  )}

                  <div className="space-y-1">
                    <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 font-medium text-gray-700">
                      <span>🏠</span> Home
                    </Link>
                    <Link to="/orders" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 font-medium text-gray-700">
                      <span>📦</span> My Orders
                    </Link>
                    <Link to="/profile" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 font-medium text-gray-700">
                      <span>👤</span> Profile Settings
                    </Link>
                    {currentRole !== 'customer' && (
                      <Link 
                        to={getDashboardPath(currentRole)} 
                        onClick={() => setIsMobileMenuOpen(false)} 
                        className="flex items-center gap-3 p-3 rounded-lg bg-orange-50 text-orange-700 font-bold mt-2"
                      >
                         {currentRole === 'chef' ? <ChefHat size={20}/> : <Truck size={20}/>} Open Dashboard
                      </Link>
                    )}
                  </div>
                </>
              ) : (
                <div className="space-y-3 mt-4">
                  <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="block w-full min-h-[44px] py-3 border-2 border-orange-100 text-orange-600 text-center font-bold rounded-xl">Login</Link>
                  <Link to="/signup" onClick={() => setIsMobileMenuOpen(false)} className="block w-full min-h-[44px] py-3 bg-orange-600 text-white text-center font-bold rounded-xl shadow-lg">Sign Up</Link>
                </div>
              )}
            </div>
            
            {user && (
              <div className="border-t border-gray-100 pt-4 mt-4">
                <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 py-3 text-red-600 font-bold bg-red-50 rounded-xl hover:bg-red-100">
                  <LogOut size={18} /> Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};